import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PermissionGate from "../../../components/common/PermissionGate";
import { PERMISSIONS } from "../../../constants/permissions";
import WorkflowCard from "../../../components/workflows/WorkflowCard";
import CreateWorkflowModal from "../../../components/workflows/CreateWorkflowModal";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import api from "../../../api/client";

const STATUS_TABS = ["All", "DRAFT", "ACTIVE", "ARCHIVED"];

export default function WorkflowManagementPage() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [activeStatus, setActiveStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeStatus !== "All") params.set("status", activeStatus);
      if (search.trim()) params.set("search", search.trim());
      const query = params.toString();
      const resp = await api.get(`/workflows${query ? `?${query}` : ""}`);
      setWorkflows(resp.data || []);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }, [api, activeStatus, search]);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const counts = useMemo(() => {
    const map = { All: workflows.length, DRAFT: 0, ACTIVE: 0, ARCHIVED: 0 };
    workflows.forEach((workflow) => {
      if (map[workflow.status] !== undefined) map[workflow.status] += 1;
    });
    return map;
  }, [workflows]);

  const onCreate = ({ name, description }) => {
    setShowCreate(false);
    navigate(
      `/workflow-builder?new=true&name=${encodeURIComponent(name)}&description=${encodeURIComponent(description || "")}`,
    );
  };

  const onEdit = (workflow) => navigate(`/workflow-builder/${workflow.id}`);

  const onRun = async (workflow) => {
    try {
      const resp = await api.post("/workflow-runs", {
        workflowId: workflow.id,
      });
      navigate(`/workflow-runner/${resp.data.runId}`);
    } catch (err) {
      alert(err.response?.data?.message || "Unable to start workflow run");
    }
  };

  const onStatus = async (workflow, status) => {
    try {
      await api.patch(`/workflows/${workflow.id}/status`, { status });
      loadWorkflows();
    } catch (err) {
      alert(err.response?.data?.message || "Status update failed");
    }
  };

  const onDuplicate = async (workflow) => {
    try {
      const full = await api.get(`/workflows/${workflow.id}`);
      const body = {
        name: `Copy of ${workflow.name}`,
        description: workflow.description || "",
        status: "DRAFT",
        definition: full.data.definition,
      };
      await api.post("/workflows", body);
      loadWorkflows();
    } catch (err) {
      alert(err.response?.data?.message || "Duplicate failed");
    }
  };

  const onDelete = (workflow) => {
    setDeleteConfirm(workflow);
  };

  const doDelete = async (workflow) => {
    try {
      await api.delete(`/workflows/${workflow.id}`);
      loadWorkflows();
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    }
  };

  return (
    <PermissionGate permission={PERMISSIONS.WORKFLOW_MANAGE_VIEW}>
      <div
        style={{
          padding: 20,
          height: "100%",
          overflow: "auto",
          background: "#f5f8fc",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <h1 style={{ margin: 0 }}>Workflow Management</h1>
          <PermissionGate permission={PERMISSIONS.WORKFLOW_MANAGE_CREATE}>
            <button
              className="btn-primary"
              type="button"
              onClick={() => setShowCreate(true)}
            >
              + Create Workflow
            </button>
          </PermissionGate>
        </div>

        <div
          style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          {STATUS_TABS.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setActiveStatus(status)}
              style={{
                border: "1px solid #d6dfed",
                background: status === activeStatus ? "#0f62fe" : "#fff",
                color: status === activeStatus ? "#fff" : "#1f2937",
                borderRadius: 999,
                padding: "7px 12px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {status} ({counts[status] || 0})
            </button>
          ))}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search workflows..."
            style={{
              marginLeft: "auto",
              minWidth: 260,
              padding: "8px 12px",
              border: "1px solid #d6dfed",
              borderRadius: 8,
            }}
          />
        </div>

        <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
          {loading && <div>Loading workflows...</div>}
          {!loading && workflows.length === 0 && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e4ebf5",
                borderRadius: 12,
                padding: 20,
              }}
            >
              No workflows found.
            </div>
          )}
          {!loading &&
            workflows.map((workflow) => (
              <WorkflowCard
                key={workflow.id}
                workflow={workflow}
                onEdit={onEdit}
                onRun={onRun}
                onActivate={(w) => onStatus(w, "ACTIVE")}
                onArchive={(w) => onStatus(w, "ARCHIVED")}
                onRestore={(w) => onStatus(w, "ACTIVE")}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
              />
            ))}
        </div>

        {showCreate && (
          <div className="rm-dialog-overlay">
            <div className="rm-dialog rm-modal-content">
              <h4 className="rm-dialog-head"> Create Workflow</h4>

              <div className="rm-dialog-body">
                <CreateWorkflowModal
                  onClose={() => setShowCreate(false)}
                  onConfirm={onCreate}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {deleteConfirm && (
        <ConfirmDialog
          title="Delete Workflow"
          message={`Delete workflow "${deleteConfirm.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmClass="btn-danger"
          onConfirm={() => { const w = deleteConfirm; setDeleteConfirm(null); doDelete(w); }}
          onClose={() => setDeleteConfirm(null)}
        />
      )}
    </PermissionGate>
  );
}
